import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { queryKeys } from '@/api/queries'
import type { QuoteBuilderApi } from '../api/client'
import type { ReceivablesApi } from '@/features/receivables/api'

// A quote's customer emails (USA-1094): PO-system conversations from the salesperson's mailbox.

type Schemas = components['schemas']

export type QuoteEmails = Schemas['QbQuoteEmailsRead']
export type QuoteThread = QuoteEmails['threads'][number]
export type QuoteAction = Schemas['QbQuoteActionRead']
export type RecordSource = Schemas['QbQuoteRecordCreate']['source']

/** Under the communications key, so every PO thread action (reply, approve, close…) refreshes it. */
export const quoteEmailsQueryKey = (quoteId: string, invoiceId?: string) =>
  [...queryKeys.communications.all, 'quote', quoteId, invoiceId ?? 'all'] as const

export const quoteEmailsQueryOptions = (api: QuoteBuilderApi, quoteId: string, invoiceId?: string) =>
  queryOptions({
    queryKey: quoteEmailsQueryKey(quoteId, invoiceId),
    queryFn: ({ signal }) =>
      api.request<QuoteEmails>(`/quotes/${quoteId}/emails${invoiceId ? `?invoice_id=${invoiceId}` : ''}`, {
        signal,
      }),
    // New replies arrive from Outlook; while the assistant reads one, check every few seconds.
    refetchInterval: (query) =>
      query.state.data?.threads.some((thread) => thread.analysis.length > 0) ? 4_000 : 60_000,
  })

/** The emails of an invoice cut with no quote (USA-378), from AR Invoices. */
export const invoiceEmailsQueryOptions = (api: ReceivablesApi, invoiceId: string) =>
  queryOptions({
    queryKey: quoteEmailsQueryKey('none', invoiceId),
    queryFn: ({ signal }) => api<QuoteEmails>(`/${invoiceId}/emails`, { signal }),
    refetchInterval: 60_000,
  })

export const recordInvoiceReply = (
  api: ReceivablesApi,
  invoiceId: string,
  threadUuid: string,
  bodyText: string,
  source: RecordSource,
) =>
  api<QuoteEmails>(`/${invoiceId}/emails/${threadUuid}/record`, {
    method: 'POST',
    body: JSON.stringify({ body_text: bodyText, source }),
  })

export function openActionCount(emails: QuoteEmails | undefined) {
  return (emails?.actions ?? []).filter((action) => action.status === 'open').length
}

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

export const quoteEmailsApi = {
  start: (api: QuoteBuilderApi, quoteId: string, subject: string, bodyText: string) =>
    api.request<QuoteEmails>(`/quotes/${quoteId}/emails`, post({ subject, body_text: bodyText })),
  record: (api: QuoteBuilderApi, quoteId: string, threadUuid: string, bodyText: string, source: RecordSource) =>
    api.request<QuoteEmails>(`/quotes/${quoteId}/emails/${threadUuid}/record`, post({ body_text: bodyText, source })),
  updateAction: (api: QuoteBuilderApi, quoteId: string, actionId: number, status: 'resolved' | 'dismissed') =>
    api.request<QuoteEmails>(`/quotes/${quoteId}/emails/actions/${actionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
}

/** "Hi Anh," from the customer's contact name, the way the salesperson would write it. */
export function customerGreeting(thread: QuoteThread) {
  const first = (thread.recipient_name ?? '').trim().split(/\s+/)[0]
  return first ? `Hi ${first},` : 'Hello,'
}
